import { getProvider } from '../svpProvider';
import { getLogger } from '../logger';

import { isSuccessResponse, requestAPI } from './shared/requestAPI';
import { fetchTags } from './tags';
import { createUrlPartial, toSearchParams } from './shared/utils';

import type {
  EnrichedTag,
  EnrichmentInput,
  EnrichmentResponse,
} from '../../types';

const logger = getLogger('enrichmentsApi');

export async function fetchTagsEnrichments() {
  try {
    const result = await requestAPI<EnrichmentResponse[]>(
      `/${getProvider()}/admin/enrichments`
    );

    if (isSuccessResponse(result)) {
      return result!;
    }
    throw result;
  } catch (error) {
    logger.error('Failed to fetch tags enrichments');
    throw error;
  }
}

export async function fetchTagEnrichment(tagId: string) {
  try {
    const result = await requestAPI<EnrichmentResponse>(
      `/${getProvider()}/admin/enrichments/${tagId}`
    );

    if (isSuccessResponse(result)) {
      return result!;
    }
    throw result;
  } catch (error) {
    logger.error('Failed to fetch tag enrichment', { tagId });
    throw error;
  }
}

export async function createTagEnrichment(fields: Partial<EnrichmentInput>) {
  try {
    const result = await requestAPI<EnrichmentResponse>(
      `/${getProvider()}/admin/enrichments`,
      {
        method: 'POST',
        body: JSON.stringify(fields),
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (isSuccessResponse(result)) {
      return result!;
    }
    throw result;
  } catch (error) {
    logger.error('Failed to create tag enrichment', { fields });
    throw error;
  }
}

export async function updateTagEnrichment(
  tagId: string,
  fields: Partial<EnrichmentInput>
) {
  try {
    const result = await requestAPI<EnrichmentResponse>(
      `/${getProvider()}/admin/enrichments/${tagId}`,
      {
        method: 'PATCH',
        body: JSON.stringify(fields),
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (isSuccessResponse(result)) {
      return result!;
    }
    throw result;
  } catch (error) {
    logger.error('Failed to update tag', { tagId, fields });
    throw error;
  }
}

export async function fetchProfiles(
  categoryId?: number
): Promise<EnrichedTag[]> {
  const params = toSearchParams({ categoryId });

  try {
    const enrichments = await requestAPI<EnrichmentResponse[]>(
      createUrlPartial(`/${getProvider()}/admin/enrichments/profiles`, params)
    );

    if (isSuccessResponse(enrichments)) {
      if (enrichments) {
        const ids = enrichments.map((enrichment) => enrichment.tagId);
        const tags = await fetchTags({ ids });
        if (tags && isSuccessResponse(tags)) {
          return tags.map((tag) => ({
            ...enrichments.find((enrichment) => enrichment.tagId === tag.id)!,
            ...tag,
            id: tag.id,
          }));
        }
      }

      throw new Error('Failed to fetch enrichments or tags');
    }

    throw enrichments;
  } catch (error) {
    logger.error('Failed to fetch profiles', { categoryId });
    throw error;
  }
}
